/**
 * Pure helpers for Super Admin in-memory route state (pre-URL-router era).
 * Characterization extraction — must preserve App.jsx behavior exactly.
 */

export const TEZUS_ROUTE_STORAGE_KEY = 'tezus-route';

export const SA_SAFE_ROUTES = new Set([
  'dashboard', 'lojas', 'loja', 'cockpit', 'nova-loja', 'planos', 'planos-permissoes',
  'inadimplencia', 'billing', 'perfis', 'equipe', 'usuarios', 'config', 'auditoria',
  'integrations', 'observabilidade', 'repairs', 'tickets', 'knowledge-base',
  'support-reports', 'notificacoes-ops', 'tech-spec',
]);

/**
 * What App currently persists to localStorage on every route change.
 * Intentionally omits: store object, tenantContext, openTicketId, contextTicket,
 * articleContext, redirect flags, pendingStore, and any in-page tabs/filters.
 */
export function buildPersistedRoute(route = {}) {
  return {
    page: route.page,
    storeId: route.store?.id,
  };
}

/**
 * Load and normalize the route saved in localStorage.
 *
 * @param {object} deps injectable collaborators (defaults keep Node tests hermetic)
 */
export function loadSavedRoute({
  storageKey = TEZUS_ROUTE_STORAGE_KEY,
  getItem,
  removeItem,
  findStoreById,
  isRealTenantsMode,
  isRouteAllowedInMvp,
  isMockOnlyPage,
  isFuturePage,
  getShowFuturePages,
  resolveLegacyRoute,
} = {}) {
  const read = typeof getItem === 'function' ? getItem : () => null;
  const clear = typeof removeItem === 'function' ? removeItem : () => {};

  try {
    const raw = read(storageKey);
    const saved = JSON.parse(raw == null ? 'null' : raw);
    if (!saved || !saved.page || !SA_SAFE_ROUTES.has(saved.page)) {
      return { page: 'dashboard' };
    }
    if (typeof isRouteAllowedInMvp === 'function' && !isRouteAllowedInMvp(saved.page)) {
      return { page: 'dashboard', mvpRedirect: true };
    }
    if (typeof isMockOnlyPage === 'function' && isMockOnlyPage(saved.page)) {
      return { page: 'dashboard', mockOnlyRedirect: true };
    }
    if (
      typeof isFuturePage === 'function'
      && isFuturePage(saved.page)
      && typeof getShowFuturePages === 'function'
      && !getShowFuturePages()
    ) {
      return { page: 'dashboard', futureRedirect: true };
    }
    const resolvedPage = typeof resolveLegacyRoute === 'function'
      ? resolveLegacyRoute(saved.page)
      : saved.page;
    if (resolvedPage !== saved.page) {
      return { page: resolvedPage, legacyRedirect: true, legacyFrom: saved.page };
    }
    if ((saved.page === 'loja' || saved.page === 'cockpit') && saved.storeId) {
      if (typeof isRealTenantsMode === 'function' && isRealTenantsMode()) {
        return { page: saved.page, storeId: saved.storeId, pendingStore: true };
      }
      const s = typeof findStoreById === 'function' ? findStoreById(saved.storeId) : null;
      if (s) return { page: saved.page, store: s };
      return { page: 'lojas', storeNotFound: true };
    }
    return { page: saved.page };
  } catch (e) {
    try { clear(storageKey); } catch (_) {}
    return { page: 'dashboard' };
  }
}
